import { computed, observable } from 'mobx'

import ModelStore from '../../../shared-lib/stores/ModelStore'
import { CrmMessageType, CrmUsageType } from '../../../graphQL/generated/enums'
import { $RootStore } from '../../../src/stores/RootStore'

export const titles: Record<CrmMessageType, string> = {
  [CrmMessageType.Accepted]: 'Booking Confirmed',
  [CrmMessageType.Reminder]: 'Booking Reminder',
  [CrmMessageType.Cancelled]: 'Booking Cancelled',
  [CrmMessageType.Completed]: 'Booking Completed',
  [CrmMessageType.Consultation]: 'Consultation Form Reminder',
  [CrmMessageType.Updated]: 'Booking Updated',
  [CrmMessageType.Broadcast]: 'Broadcast Campaign',
  [CrmMessageType.Automated]: 'Automated Campaign',
}

const GOOD_STATUSES = ['sent', 'delivered', 'clicked', 'opened']

export const SKIPPED_NOT_WHITELISTED_STATUS = 'skipped_not_whitelisted'

type EmailTemplates = Record<string, { label: string }> | undefined

export const crmUsageTitle = (data: Pick<CrmUsage, 'automatedTriggerType' | 'messageType'>, emailTemplates: EmailTemplates) => {
  const { automatedTriggerType, messageType } = data

  if (automatedTriggerType && automatedTriggerType.includes('tr_')) {
    return emailTemplates?.[automatedTriggerType]?.label ?? 'Automated Message'
  }

  return titles[messageType]
}

export const crmUsageStatus = (data: Pick<CrmUsage, 'usageType' | 'deliveryStatus'>) => {
  const { usageType, deliveryStatus } = data

  if (deliveryStatus === SKIPPED_NOT_WHITELISTED_STATUS) return 'Not whitelisted'
  if (usageType === CrmUsageType.Email) return deliveryStatus

  return 'Sent'
}

export const isGoodCrmUsageStatus = (status: string | null | undefined) =>
  GOOD_STATUSES.includes(status?.toLowerCase() ?? '')

export default class CrmUsageModel extends ModelStore {
  @observable accessor data: CrmUsage = {
    _id: '',
    externalBookingId: '',
    externalClientId: '',
    emailCampaignId: '',
    professionalId: '',
    usageType: CrmUsageType.Sms,
    messageType: CrmMessageType.Accepted,
    price: 0,
    createdAt: Date.now(),
  }

  constructor(data: Partial<CrmUsage>, rootStore: $RootStore) {
    super(data, rootStore)
    this.merge(data)
  }

  @computed get externalBooking() {
    const { externalBookingId } = this.data
    if (!externalBookingId) return
    return this.rootStore.externalBookingStore.findItemById(externalBookingId)
  }

  @computed get externalClient() {
    const { externalClientId } = this.data
    if (!externalClientId) return
    return this.rootStore.externalClientStore.findItemById(externalClientId)
  }

  @computed get emailCampaign() {
    const { emailCampaignId } = this.data
    if (!emailCampaignId) return
    return this.rootStore.emailCampaignStore.findItemById(emailCampaignId)
  }

  @computed get smsCampaign() {
    const { smsCampaignId } = this.data
    if (!smsCampaignId) return
    return this.rootStore.smsCampaignStore.findItemById(smsCampaignId)
  }

  @computed get title() {
    return crmUsageTitle(this.data, this.rootStore.emailCampaignStore.emailTemplates)
  }

  @computed get status() {
    return crmUsageStatus(this.data)
  }

  @computed get isGoodStatus() {
    return isGoodCrmUsageStatus(this.status)
  }

  @computed get isSkippedNotWhitelisted() {
    return this.data.deliveryStatus === SKIPPED_NOT_WHITELISTED_STATUS
  }
}
